/**
 * Busca da config pública.
 *
 * O `403` não é tratado como "erro de rede": ele é a resposta esperada quando o site não está na
 * allowlist, e precisa chegar ao console com uma explicação que resolva o problema de quem instalou
 * o widget. Falhar em silêncio aqui geraria horas de suporte.
 */

import type { WebchatConfig } from '@merlin/flow-engine';

export type FetchConfigResult =
  | { ok: true; config: WebchatConfig }
  | { ok: false; status: number; error: string; message: string };

interface ConfigPayload {
  config?: WebchatConfig;
  error?: string;
  message?: string;
}

export interface FetchConfigOptions {
  timeoutMs?: number;
  /**
   * Ignora cache na busca da config. Ligado por `data-no-cache="true"` na tag do script.
   *
   * Existe porque a config é servida com `max-age` (ver `CONFIG_CACHE_SECONDS` no engine) **e** fica no
   * CDN do Hosting: depois de publicar, a mudança leva alguns segundos para aparecer numa página que já
   * foi carregada. Recarregar à força resolve o cache do navegador, mas não o do CDN — só uma URL
   * diferente resolve, e é o que o parâmetro descartável faz.
   *
   * Fora de teste isto fica desligado de propósito: sem cache, cada visita do visitante vira uma
   * invocação da Function.
   */
  noCache?: boolean;
}

export async function fetchConfig(
  apiBase: string,
  publicKey: string,
  options: number | FetchConfigOptions = {},
): Promise<FetchConfigResult> {
  // Aceita o número solto que a assinatura antiga usava, para não quebrar chamada existente.
  const { timeoutMs = 10_000, noCache = false } =
    typeof options === 'number' ? { timeoutMs: options } : options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const base = `${apiBase.replace(/\/$/, '')}/getConfig?key=${encodeURIComponent(publicKey)}`;
  // `_` descartável: muda a URL, que é a chave do cache do CDN. Sem isso o `no-store` do fetch
  // convence só o navegador.
  const url = noCache ? `${base}&_=${Date.now()}` : base;

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      mode: 'cors',
      signal: controller.signal,
      ...(noCache ? { cache: 'no-store' as RequestCache } : {}),
    });

    /*
     * HTML onde deveria vir JSON: quase sempre `data-api-base` errado ou ausente.
     *
     * Vale um erro próprio porque o sintoma default engana feio. Sem `data-api-base`, o bootstrap adivinha
     * `<origem do script>/api` — e no nosso Hosting esse caminho cai no rewrite `**` do SPA, que devolve o
     * `index.html` com **200**. Então: status ok, `json()` falha, `payload` fica nulo, e a mensagem que
     * chegava ao desenvolvedor era "O backend respondeu sem a config" — mandando investigar a publicação
     * no painel, quando o defeito está no atributo do snippet.
     *
     * A checagem exige `text/html` explícito, e não "não é json": resposta legítima sem `content-type`
     * (mock de teste, servidor caseiro) continua caindo no caminho normal.
     */
    const tipoDoConteudo = response.headers.get('content-type') ?? '';
    if (tipoDoConteudo.includes('text/html')) {
      return {
        ok: false,
        status: response.status,
        error: 'not_json',
        message:
          `${apiBase} respondeu HTML, não JSON (HTTP ${response.status}). ` +
          'Isso costuma significar que data-api-base está errado ou faltando no script do widget.',
      };
    }

    const payload = (await response.json().catch(() => null)) as ConfigPayload | null;

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: payload?.error ?? 'http_error',
        message: payload?.message ?? `A requisição de config falhou com HTTP ${response.status}.`,
      };
    }

    if (!payload?.config) {
      return {
        ok: false,
        status: response.status,
        error: 'invalid_payload',
        message: 'O backend respondeu sem a config.',
      };
    }

    return { ok: true, config: payload.config };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, status: 0, error: 'timeout', message: `A config não chegou em ${timeoutMs}ms.` };
    }
    return {
      ok: false,
      status: 0,
      error: 'network',
      message: `Não foi possível buscar a config em ${url}. (${error instanceof Error ? error.message : String(error)})`,
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Id de sessão só para correlacionar chamadas de webhook nos logs. Não identifica a pessoa. */
export function createSessionId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') return cryptoObj.randomUUID();
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
